// src/components/PrintDesigner/printVariables.js
// المتغيرات المتاحة في مصمم الطباعة — تُكتب في أي نص بالشكل {CompanyName}
import dayjs from "dayjs";
import { tafqeet } from "../../utils/tafqeet";

export const money = (v) =>
  Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const date = (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "");
const time = (v) => (v ? dayjs(v).format("HH:mm") : "");
const num = (v) => Number(v) || 0;

const itemsOf = (d) => (Array.isArray(d?.items) ? d.items : []);
const subtotal = (d) => d?.totals?.subtotal ?? itemsOf(d).reduce((s, i) => s + num(i.quantity) * num(i.unitPrice), 0);
const discount = (d) => d?.totals?.discountTotal ?? itemsOf(d).reduce((s, i) => s + num(i.discount), 0);
const vat = (d) => d?.totals?.totalVAT ?? itemsOf(d).reduce((s, i) => s + num(i.taxValue), 0);
const net = (d) => d?.totals?.net ?? subtotal(d) - discount(d) + vat(d);

// ── قراءة مرنة من بطاقة الشركة / العميل: تقبل أكثر من اسم للحقل (camelCase / PascalCase / snake_case)
const norm = (k) => String(k).toLowerCase().replace(/[_\s-]/g, "");
export function field(obj, ...names) {
  if (!obj || typeof obj !== "object") return undefined;
  const map = {};
  Object.keys(obj).forEach(k => { map[norm(k)] = obj[k]; });
  for (const n of names) {
    const v = map[norm(n)];
    if (v != null && String(v).trim() !== "") return v;
  }
  return undefined;
}
const co = (d) => d?.company || d?.companyProfile || {};
const cu = (d) => d?.customer || d?.client || {};

// حقول العنوان الوطني (هيئة الزكاة) — نفس الأسماء للشركة والعميل
const ADDR = {
  building: ["buildingNumber", "buildingNo", "building"],
  street: ["streetName", "street", "streetAr"],
  district: ["district", "districtName", "neighborhood", "citySubdivisionName"],
  city: ["city", "cityName", "cityAr"],
  postal: ["postalCode", "postalZone", "zipCode", "zip"],
  additional: ["additionalNumber", "additionalNo", "plotIdentification"],
  country: ["country", "countryName", "countryAr"],
};
const fullAddress = (o) => {
  const a = (k) => field(o, ...ADDR[k]);
  const parts = [
    [a("building"), a("street")].filter(Boolean).join(" "),
    a("district"),
    [a("city"), a("postal")].filter(Boolean).join(" "),
    a("country"),
  ].filter(Boolean);
  return parts.length ? parts.join("، ") : field(o, "address", "addressAr", "fullAddress");
};

// ── الحقول المخصصة للفاتورة (من /Invoice/print → customFields: [{ key, label, value }])
const customFieldsOf = (d) => (Array.isArray(d?.customFields) ? d.customFields : []);
const customFieldValue = (d, key) =>
  customFieldsOf(d).find(f => String(f.key).toLowerCase() === String(key).toLowerCase())?.value;
const customFieldsText = (d) =>
  customFieldsOf(d).filter(f => String(f.value ?? "").trim()).map(f => `${f.label}: ${f.value}`).join("\n");

/** مجموعة متغيرات الحقول المخصصة لنوع فاتورة — تُبنى في المصمم من /InvoiceFields */
export const customFieldsGroup = (fields = []) => ({
  kind: "invoice", key: "custom", label: "الحقول المخصصة",
  items: [
    { key: "CustomFields", label: "كل الحقول المخصصة", get: d => customFieldsText(d) },
    ...fields.map(f => ({ key: `CF_${f.fieldName}`, label: f.displayName, get: d => customFieldValue(d, f.fieldName) })),
  ],
});

const VOUCHER_NAMES = { 101: "سند قبض من عميل", 102: "سند صرف لمورد", 103: "سند قبض عام", 104: "سند صرف عام", 105: "سند صرف ضريبة" };
const isReceiptVoucher = (d) => [101, 103].includes(Number(d?.voucherType));
const voucherTotal = (d) => (d?.totalAmount != null ? num(d.totalAmount) : num(d?.amount) + num(d?.taxAmount));

export const VARIABLE_GROUPS = [
  {
    key: "company", label: "المنشأة",
    items: [
      { key: "CompanyName", label: "اسم المنشأة", get: d => field(co(d), "name", "nameAr", "arabicName", "companyName", "companyNameAr", "tradeName", "tradeNameAr") },
      { key: "CompanyNameEn", label: "اسم المنشأة (EN)", get: d => field(co(d), "nameEn", "englishName", "companyNameEn", "nameEnglish", "tradeNameEn") },
      { key: "CompanyTax", label: "الرقم الضريبي", get: d => field(co(d), "taxNumber", "vatNumber", "vatNo", "vatRegistrationNumber", "taxRegistrationNumber", "taxNo", "taxId") },
      { key: "CompanyCR", label: "السجل التجاري", get: d => field(co(d), "crNumber", "commercialRegistration", "commercialRegister", "commercialRecord", "crNo", "commercialRegistrationNumber", "commercialRegNo", "crn") },
      { key: "CompanyActivity", label: "النشاط التجاري", get: d => field(co(d), "activityAr", "activity", "businessActivity") },
      { key: "CompanyActivityEn", label: "النشاط التجاري (EN)", get: d => field(co(d), "activityEn") },
      { key: "CompanyPhone", label: "الهاتف", get: d => field(co(d), "phone", "telephone", "phoneNumber", "tel", "phone1") },
      { key: "CompanyMobile", label: "الجوال", get: d => field(co(d), "mobile", "mobileNumber") },
      { key: "CompanyFax", label: "الفاكس", get: d => field(co(d), "fax") },
      { key: "CompanyEmail", label: "البريد", get: d => field(co(d), "email") },
      { key: "CompanyWebsite", label: "الموقع", get: d => field(co(d), "website", "webSite", "url") },
      { key: "CompanyAddress", label: "العنوان", get: d => field(co(d), "address", "addressAr", "fullAddress", "addressLine") },
      { key: "CompanyAddressEn", label: "العنوان (EN)", get: d => field(co(d), "addressEn", "englishAddress", "addressEnglish") },
      { key: "CompanyFullAddress", label: "العنوان الوطني كامل", get: d => fullAddress(co(d)) },
      { key: "CompanyBuildingNo", label: "رقم المبنى", get: d => field(co(d), ...ADDR.building) },
      { key: "CompanyStreet", label: "الشارع", get: d => field(co(d), ...ADDR.street) },
      { key: "CompanyDistrict", label: "الحي", get: d => field(co(d), ...ADDR.district) },
      { key: "CompanyCity", label: "المدينة", get: d => field(co(d), ...ADDR.city) },
      { key: "CompanyPostalCode", label: "الرمز البريدي", get: d => field(co(d), ...ADDR.postal) },
      { key: "CompanyAdditionalNo", label: "الرقم الإضافي", get: d => field(co(d), ...ADDR.additional) },
      { key: "CompanyCountry", label: "الدولة", get: d => field(co(d), ...ADDR.country) },
    ],
  },
  {
    kind: "invoice", key: "invoice", label: "الفاتورة",
    items: [
      { key: "InvoiceTitle", label: "عنوان الفاتورة", get: d => d?.invoiceTitle || "فاتورة ضريبية" },
      { key: "InvoiceNumber", label: "رقم الفاتورة", get: d => d?.invoiceNumber },
      { key: "IssueDate", label: "التاريخ", get: d => date(d?.issueDate) },
      { key: "IssueTime", label: "الوقت", get: d => time(d?.issueDate) },
      { key: "ReferenceNumber", label: "رقم المرجع", get: d => d?.referenceNumber },
      { key: "PaymentMethod", label: "طريقة الدفع", get: d => d?.paymentMethodName || d?.paymentMethod },
      { key: "Salesman", label: "البائع", get: d => d?.salesmanName },
      { key: "Notes", label: "الملاحظات", get: d => d?.notes },
      { key: "User", label: "المستخدم", get: d => d?.userName || localStorage.getItem("userName") },
      { key: "BranchName", label: "الفرع", get: d => d?.branchName },
      { key: "StockName", label: "المخزن", get: d => d?.stockName },
      { key: "CustomFields", label: "الحقول المخصصة (كلها)", get: d => customFieldsText(d) },
    ],
  },
  {
    kind: "invoice", key: "customer", label: "العميل",
    items: [
      { key: "CustomerName", label: "اسم العميل", get: d => field(cu(d), "name", "nameAr", "arabicName", "customerName") || d?.partyName },
      { key: "CustomerNameEn", label: "اسم العميل (EN)", get: d => field(cu(d), "nameEn", "englishName") },
      { key: "CustomerCode", label: "رقم / كود العميل", get: d => field(cu(d), "code", "customerCode", "accountNumber", "customerNo") },
      { key: "CustomerTax", label: "الرقم الضريبي", get: d => field(cu(d), "taxNumber", "vatNumber", "vatNo") },
      { key: "CustomerCR", label: "السجل التجاري", get: d => field(cu(d), "crNumber", "commercialRegistration", "commercialRegister", "commercialRecord", "crNo") },
      { key: "CustomerMobile", label: "الجوال", get: d => field(cu(d), "mobile", "mobileNumber", "phone") },
      { key: "CustomerPhone", label: "الهاتف", get: d => field(cu(d), "phone", "telephone", "phoneNumber") },
      { key: "CustomerEmail", label: "البريد", get: d => field(cu(d), "email") },
      { key: "CustomerAddress", label: "العنوان", get: d => field(cu(d), "address", "addressAr") },
      { key: "CustomerFullAddress", label: "العنوان الوطني كامل", get: d => fullAddress(cu(d)) },
      { key: "CustomerBuildingNo", label: "رقم المبنى", get: d => field(cu(d), ...ADDR.building) },
      { key: "CustomerStreet", label: "الشارع", get: d => field(cu(d), ...ADDR.street) },
      { key: "CustomerDistrict", label: "الحي", get: d => field(cu(d), ...ADDR.district) },
      { key: "CustomerCity", label: "المدينة", get: d => field(cu(d), ...ADDR.city) },
      { key: "CustomerPostalCode", label: "الرمز البريدي", get: d => field(cu(d), ...ADDR.postal) },
      { key: "CustomerAdditionalNo", label: "الرقم الإضافي", get: d => field(cu(d), ...ADDR.additional) },
      { key: "CustomerCountry", label: "الدولة", get: d => field(cu(d), ...ADDR.country) },
      { key: "CustomerPrevBalance", label: "الرصيد السابق", get: d => (d?.customerPrevBalance != null ? money(d.customerPrevBalance) : undefined) },
    ],
  },
  {
    kind: "invoice", key: "totals", label: "الإجماليات",
    items: [
      { key: "TotalBeforeTax", label: "الإجمالي قبل الخصم", get: d => money(subtotal(d)) },
      { key: "TotalDiscount", label: "إجمالي الخصم", get: d => money(discount(d)) },
      { key: "TotalAfterDiscount", label: "بعد الخصم", get: d => money(subtotal(d) - discount(d)) },
      { key: "TotalTax", label: "إجمالي الضريبة", get: d => money(vat(d)) },
      { key: "NetTotal", label: "صافي الفاتورة", get: d => money(net(d)) },
      { key: "NetTotalWords", label: "الصافي كتابةً", get: d => tafqeet(net(d)) },
      { key: "PromotionSavings", label: "وفّرت لك (العروض)", get: d => (num(d?.promotionSavings) > 0 ? money(d.promotionSavings) : undefined) },
      { key: "PaidAmount", label: "المدفوع", get: d => money(d?.paidAmount) },
      { key: "RemainingAmount", label: "المتبقي", get: d => money(num(net(d)) - num(d?.paidAmount)) },
      { key: "ItemsCount", label: "عدد الأصناف", get: d => String(itemsOf(d).length) },
      { key: "TotalQty", label: "إجمالي الكميات", get: d => String(itemsOf(d).reduce((s, i) => s + num(i.quantity), 0)) },
    ],
  },
  {
    key: "voucher", label: "السند", kind: "voucher",
    items: [
      { key: "VoucherType", label: "نوع السند", get: d => d?.voucherTypeName || VOUCHER_NAMES[Number(d?.voucherType)] },
      { key: "VoucherNumber", label: "رقم السند", get: d => d?.voucherNumber },
      { key: "VoucherDate", label: "تاريخ السند", get: d => date(d?.voucherDate) },
      { key: "VoucherTime", label: "وقت السند", get: d => time(d?.voucherDate) },
      { key: "PartyLabel", label: "استلمنا من / صرفنا إلى", get: d => (isReceiptVoucher(d) ? "استلمنا من السيد/السادة:" : "صرفنا إلى السيد/السادة:") },
      { key: "PartyName", label: "اسم الطرف", get: d => d?.customerName || d?.supplierName || d?.accountName || d?.partyName },
      { key: "VoucherTotal", label: "المبلغ الإجمالي", get: d => money(voucherTotal(d)) },
      { key: "VoucherTotalWords", label: "المبلغ كتابةً", get: d => tafqeet(voucherTotal(d)) },
      { key: "VoucherNet", label: "المبلغ قبل الضريبة", get: d => (num(d?.taxAmount) ? money(d?.amount) : undefined) },
      { key: "VoucherTax", label: "الضريبة", get: d => (num(d?.taxAmount) ? money(d?.taxAmount) + (d?.vatName ? ` (${d.vatName})` : "") : undefined) },
      { key: "VoucherPayment", label: "طريقة الدفع", get: d => d?.paymentTypeName || (Number(d?.paymentType) === 2 ? "بنكي" : "نقدي") },
      { key: "VoucherSafeOrBankLabel", label: "عنوان: الصندوق / البنك", get: d => (Number(d?.paymentType) === 2 ? "البنك" : "الصندوق") },
      { key: "VoucherSafeOrBank", label: "الصندوق أو البنك", get: d => d?.bankName || d?.safeName },
      { key: "VoucherReference", label: "رقم المرجع", get: d => d?.referenceNumber },
      { key: "VoucherReferenceDate", label: "تاريخ المرجع", get: d => date(d?.referenceDate) },
      { key: "VoucherDescription", label: "البيان", get: d => d?.description || d?.notes },
      { key: "SignatureMiddle", label: "توقيع: أمين الصندوق / المدير المالي", get: d => (isReceiptVoucher(d) ? "أمين الصندوق" : "المدير المالي") },
      { key: "SignatureParty", label: "توقيع: الدافع / المستلم", get: d => (isReceiptVoucher(d) ? "الدافع" : "المستلم") },
    ],
  },
  {
    key: "other", label: "أخرى",
    items: [
      { key: "PrintDate", label: "تاريخ الطباعة", get: () => dayjs().format("DD/MM/YYYY HH:mm") },
    ],
  },
];

/** مجموعات الحقول حسب نوع المستند — "invoice" | "voucher" (المنشأة وأخرى مشتركة) */
export const variableGroupsFor = (kind = "invoice") => VARIABLE_GROUPS.filter(g => !g.kind || g.kind === kind);

export const VARIABLES = Object.fromEntries(VARIABLE_GROUPS.flatMap(g => g.items.map(i => [i.key, i])));

/**
 * يستبدل {Key} بقيمته — المتغير غير المعروف يبقى كما هو.
 * السطر الذي كل متغيراته فارغة يُحذف بالكامل (مثل "السجل التجاري: {CustomerCR}" لعميل بلا سجل)
 */
export function resolveText(text, data) {
  const src = String(text ?? "");
  if (data?.__raw) return src; // وضع "إظهار المتغيرات" في المصمم
  const out = [];
  src.split("\n").forEach(line => {
    let known = 0, filled = 0;
    const r = line.replace(/\{(\w+)\}/g, (m, k) => {
      // {CF_رمز_الحقل} = قيمة حقل مخصص واحد (المفاتيح تختلف حسب نوع الفاتورة فلا تُسجَّل مسبقًا)
      const v = VARIABLES[k] || (k.startsWith("CF_") ? { get: d => customFieldValue(d, k.slice(3)) } : null);
      if (!v) return m;
      known++;
      const val = v.get(data || {});
      const str = val == null ? "" : String(val).trim();
      if (str) filled++;
      return str;
    });
    if (known > 0 && filled === 0) return;
    out.push(r);
  });
  return out.join("\n");
}

/** قيم الإجماليات الرقمية (للعنصر "الإجماليات") */
export const totalsOf = (d) => {
  const s = subtotal(d), dis = discount(d), v = vat(d), n = net(d);
  return { subtotal: s, discount: dis, afterDiscount: s - dis, vat: v, net: n, paid: num(d?.paidAmount), remaining: n - num(d?.paidAmount) };
};

// بيانات تجريبية للمعاينة داخل المصمم
export const MOCK_DATA = {
  customFields: [],   // يملؤها المصمم من حقول نوع الفاتورة المختار
  invoiceNumber: "INV-2026-001",
  issueDate: new Date().toISOString(),
  invoiceTitle: "فاتورة ضريبية",
  referenceNumber: "REF-12345",
  salesmanName: "محمد أحمد",
  paymentMethodName: "نقدي",
  notes: "البضاعة المباعة لا ترد ولا تستبدل بعد 7 أيام",
  paidAmount: 8165,
  branchName: "الفرع الرئيسي",
  company: {
    name: "شركة أرباح التقنية",
    nameEn: "Arbah Tech Co.",
    address: "الرياض - طريق الملك فهد - العليا",
    addressEn: "Olaya, King Fahd Rd, Riyadh",
    phone: "0112345678",
    email: "info@arbah.sa",
    taxNumber: "300000000000003",
    crNumber: "1010123456",
    website: "www.arbah.sa",
    mobile: "0501234567",
    buildingNumber: "7235",
    streetName: "طريق الملك فهد",
    district: "العليا",
    city: "الرياض",
    postalCode: "12211",
    additionalNumber: "3344",
    country: "المملكة العربية السعودية",
    logoUrl: null,
  },
  customer: {
    name: "شركة الأفق للتجارة",
    mobile: "0555555555",
    nameEn: "Al Ofok Trading Co.",
    code: "C-1025",
    address: "جدة - حي الروضة",
    taxNumber: "311111111111113",
    crNumber: "4030123456",
    email: "info@alofok.sa",
    buildingNumber: "2190",
    streetName: "شارع الأمير سلطان",
    district: "الروضة",
    city: "جدة",
    postalCode: "23435",
    additionalNumber: "7781",
    country: "المملكة العربية السعودية",
  },
  customerPrevBalance: 1250,
  items: [
    { arabicName: "لابتوب ديل XPS 15", unitName: "حبة", quantity: 1, unitPrice: 5000, discount: 200, taxValue: 720, description: "معالج i7، رام 16 جيجا" },
    { arabicName: "ماوس لوجيتك MX Master", unitName: "حبة", quantity: 2, unitPrice: 400, discount: 0, taxValue: 120, description: "لاسلكي، لون أسود" },
    { arabicName: "اشتراك صيانة سنوي", unitName: "سنة", quantity: 1, unitPrice: 1500, discount: 0, taxValue: 225, description: "دعم فني 24/7" },
  ],
  totals: { subtotal: 7300, discountTotal: 200, totalVAT: 1065, net: 8165 },
  qrCodeUrl: null,
};

// بيانات تجريبية لسند
export const MOCK_VOUCHER = {
  voucherType: 101,
  voucherTypeName: "سند قبض من عميل",
  voucherNumber: "RV-0012",
  voucherDate: new Date().toISOString(),
  customerName: "شركة الأفق للتجارة",
  amount: 4500,
  taxAmount: 0,
  paymentType: 1,
  safeName: "الصندوق الرئيسي",
  referenceNumber: "S-F39-00003",
  referenceDate: new Date().toISOString(),
  description: "سداد دفعة من حساب الفاتورة رقم S-F39-00003",
  branchName: "الفرع الرئيسي",
  company: MOCK_DATA.company,
};